import { BankIdDialog } from '@/components/BankIdDialog';
import { BankIdDialogTitle } from '@/components/BankIdDialogTitle';
import BankIdQR from '@/components/BankIdQR/page';
import { GeneralDialog } from '@/components/GeneralDialog';
import { OtpDialog } from '@/components/OtpDialog';
import SwedenBankIdMobileSigning from '@/components/SwedenBankIdMobileSigning';
import { GTMEventName } from '@/constants/gtm';
import { VERIFICATION_METHOD } from '@/constants/orders';
import { useCancelSignDocument } from '@/hooks/Documents/useCancelSignDocument';
import { useSignDocument } from '@/hooks/Documents/useSignDocument';
import { useGetSigningError } from '@/hooks/Signing/useGetSigningError';
import { useSignEntity } from '@/hooks/Signing/useSignEntity';
import { useSigningStateMachine } from '@/hooks/Signing/useSigningStateMachine';
import { useSendGTMEvent } from '@/hooks/useSendGTMEvents';
import { Documents } from '@/types/company';
import { isMobile } from '@/utils/devices';
import { useQueryClient } from '@tanstack/react-query';
import { SCRIVE_TYPE } from '@treyd-io/core/constants/auth';
import { NORDIC_COUNTRIES } from '@treyd-io/core/constants/country';
import { useToastNotification } from '@treyd-io/treyd-ui/hooks/useToastNotification';
import { t } from 'i18next';
import { toString } from 'lodash';
import { useSession } from 'next-auth/react';

interface SignDocumentStepProps {
  country: string;
  documentId: number;
  handleNextStep?: () => void;
  handleOnBackClick?: () => void;
  onClose?: () => void;
}

export const SignDocumentStep = ({
  country,
  handleNextStep,
  handleOnBackClick,
  onClose,
  documentId,
}: SignDocumentStepProps) => {
  const { mutate: cancelSign, isPending } = useCancelSignDocument();
  const {
    mutate: signDocument,
    isSuccess,
    error,
    isPending: isSigningDocument,
  } = useSignDocument();
  const queryClient = useQueryClient();
  const notification = useToastNotification();
  const isMobileDevice = isMobile();
  const { sendEvent } = useSendGTMEvent();
  const { data } = useSession({ required: true });
  const { switchSigningMethod, state } = useSigningStateMachine({ country });
  const { bankId, phoneOtp } = useSignEntity<
    Documents.SignDocumentPayload,
    Documents.SignDocumentErrorResponse
  >({
    signFn: (payload, options) =>
      signDocument(payload, { onSuccess, ...options }),
    cancelFn: (payload, options) => cancelSign(payload, { ...options }),
  });

  const { errorMessage, isPollingData } =
    useGetSigningError<Documents.SignDocumentErrorResponse>({
      error,
      isCancelLoading: isPending,
      isSuccess,
      isSigning: isSigningDocument,
    });

  const user = data?.user;
  const onSuccess = () => {
    onClose &&
      queryClient.invalidateQueries({
        queryKey: ['company-documents', user?.company?.id],
      });
    handleNextStep?.();
    onClose?.();
    notification({
      title: t('document_signed_successfully'),
      type: 'success',
    });
    sendEvent({
      event: GTMEventName.POA_DOCUMENT_SIGNED,
    });
  };

  const handleSwedenBankIdCancel = () =>
    bankId.onCancel({
      documentId,
      data: {
        use_qr: !isMobileDevice,
        verification_method: 'bankid_se',
        personal_no: toString(user?.identity_reference),
        identity_type: toString(user?.identity_type),
      },
    });

  const handleBankIdCancel = () =>
    bankId.onCancel({
      documentId,
      data: {
        use_qr: true,
        verification_method: `bankid_${country}`,
        personal_no: toString(user?.identity_reference),
        identity_type: toString(user?.identity_type),
      },
    });

  const handleSwedenBankIdSign = () =>
    bankId.onSign({
      documentId,
      data: {
        use_qr: !isMobileDevice,
        verification_method: 'bankid_se',
        identity_type: toString(user?.identity_type),
        personal_no: toString(user?.identity_reference),
      },
    });

  const handleBankIdSign = () =>
    bankId.onSign({
      documentId,
      data: {
        use_qr: true,
        verification_method: `bankid_${country}`,
        identity_type: toString(user?.identity_type),
        personal_no: toString(user?.identity_reference),
      },
    });

  const handlePhoneDigitsSubmit = async (phone: string) =>
    phoneOtp.onCancel({
      documentId,
      data: {
        phone_no: phone,
        use_qr: true,
        verification_method: VERIFICATION_METHOD.SMS,
        identity_type: toString(user?.identity_type),
      },
    });

  const handlePhoneOtpSubmit = async (
    otp: string,
    phone: string
  ): Promise<void> => {
    phoneOtp.onSign({
      documentId,
      data: {
        phone_no: phone,
        otp,
        use_qr: true,
        verification_method: VERIFICATION_METHOD.SMS,
        identity_type: toString(user?.identity_type),
      },
    });
    return Promise.resolve();
  };

  return (
    <GeneralDialog
      title={
        <BankIdDialogTitle
          type={SCRIVE_TYPE.SIGN}
          signingMethod={state.context.signingMethod}
        />
      }
      open={true}
      onClose={() => {
        onClose?.();
        handleOnBackClick?.();
      }}>
      {state.matches('AutomaticMethodDetection.bankid.qr') &&
        (isMobileDevice ? (
          <SwedenBankIdMobileSigning
            type={SCRIVE_TYPE.SIGN}
            switchSigningMethod={switchSigningMethod}
            cancelSignCallback={handleSwedenBankIdCancel}
            signCallback={handleSwedenBankIdSign}
            isPollingData={isPollingData}
            bankIdAppAccessUrl={bankId.scriveUrl.current}
            error={errorMessage}
          />
        ) : (
          <BankIdQR
            type={SCRIVE_TYPE.SIGN}
            switchSigningMethod={switchSigningMethod}
            cancelSignCallback={handleSwedenBankIdCancel}
            signCallback={handleSwedenBankIdSign}
            isPollingData={isPollingData}
            token={bankId.token}
            error={errorMessage}
          />
        ))}
      {state.matches('AutomaticMethodDetection.bankid.personalNumber') && (
        <BankIdDialog
          type={SCRIVE_TYPE.SIGN}
          onSignWithVerificationCodeClick={switchSigningMethod}
          cancelSignCallback={handleBankIdCancel}
          signCallback={handleBankIdSign}
          isPollingData={isPollingData}
          error={errorMessage}
        />
      )}
      {state.matches('AutomaticMethodDetection.otp') && (
        <OtpDialog
          type={SCRIVE_TYPE.SIGN}
          showActions={NORDIC_COUNTRIES.includes(country)}
          switchSigningMethod={switchSigningMethod}
          country={country}
          cancelSignCallback={() => {
            cancelSign({ documentId });
          }}
          onPhoneDigitsSubmit={handlePhoneDigitsSubmit}
          onOtpSubmit={handlePhoneOtpSubmit}
          onClickBack={async () => {
            cancelSign({ documentId });
          }}
          isLoading={isSigningDocument}
        />
      )}
    </GeneralDialog>
  );
};
